import { DealSpecificProperty, InvestmentDetailsInterface } from "../../../../../../../../models";
import { camelCaseToSentenceCase, formatNumber } from "../../../../../../../../utils";
import { Column, DynamicFormFieldsFromJsonSchema, isNumberField, Row } from "../../../../../../../../widget";

type UpdateInvestmentFn = <K extends keyof InvestmentDetailsInterface>(
  key: string,
  field: K,
  value:
    | InvestmentDetailsInterface[K]
    | ((currentValue: InvestmentDetailsInterface[K]) => InvestmentDetailsInterface[K])
) => void;

interface DynamicInvestmentDetailsProps {
  selectedInvestment: InvestmentDetailsInterface | undefined;
  dealSpecificSchemaProperties: [string, DealSpecificProperty][] | undefined;
  onUpdateInvestment: UpdateInvestmentFn;
  isReadonly: boolean;
  hasFieldError?: (fieldKey: string) => boolean;
}

const getDynamicInvestmentFieldLabel = (fieldKey: string): string => {
  if (fieldKey === "cashflowRepeater") {
    return "Cashflow";
  }

  return camelCaseToSentenceCase(fieldKey);
};

export const DynamicInvestmentDetails = ({
  selectedInvestment,
  dealSpecificSchemaProperties,
  onUpdateInvestment,
  isReadonly,
  hasFieldError,
}: DynamicInvestmentDetailsProps): JSX.Element | null => {
  if (!selectedInvestment || !dealSpecificSchemaProperties) {
    return null;
  }

  const handleFieldChange = (fieldKey: string, value: unknown): void => {
    onUpdateInvestment(selectedInvestment.key, "fields", (currentFields) => ({
      ...(currentFields ?? {}),
      [fieldKey]: value,
    }));
  };

  const handleFieldsChange = (updatedFields: Record<string, unknown>): void => {
    onUpdateInvestment(selectedInvestment.key, "fields", (currentFields) => ({
      ...(currentFields ?? {}),
      ...updatedFields,
    }));
  };

  const expressionContext = {
    ...(selectedInvestment.fields ?? {}),
    description: selectedInvestment.description,
    relatedObjects: selectedInvestment.associations.map((uuid) => ({ uuid })),
  };

  return (
    <DynamicFormFieldsFromJsonSchema
      schemaProperties={dealSpecificSchemaProperties}
      getFieldValue={(fieldKey) => selectedInvestment.fields?.[fieldKey]}
      onFieldChange={!isReadonly ? handleFieldChange : undefined}
      onFieldsChange={!isReadonly ? handleFieldsChange : undefined}
      expressionContext={expressionContext}
      isReadonly={isReadonly}
      hasFieldError={(fieldKey) => hasFieldError?.(fieldKey) ?? false}
      shouldRenderField={(fieldKey) => fieldKey !== "relatedObjects" && fieldKey !== "description"}
      getFieldLabel={getDynamicInvestmentFieldLabel}
      valueMappers={{
        number: ({ value }) => {
          if (value === "") {
            return null;
          }

          const parsedValue = Number(value);
          return Number.isNaN(parsedValue) ? null : parsedValue;
        },
        date: ({ value }) => value ?? null,
      }}
      formatReadonlyValue={({ fieldDefinition, fieldValue }) => {
        if (!isNumberField(fieldDefinition) || fieldDefinition.enum) {
          return undefined;
        }

        const hasEmptyValue =
          fieldValue === undefined || fieldValue === null || fieldValue === "" || Number.isNaN(Number(fieldValue));

        return hasEmptyValue ? undefined : formatNumber(Number(fieldValue));
      }}
      renderLayout={(fields) => (
        <Row>
          {fields.map((field, index) => (
            <Column span={field.isFullWidth ? 12 : 6} key={field.key}>
              <div className={index < fields.length - 2 ? "InvestmentDetailsFieldWrapper" : ""}>{field.element}</div>
            </Column>
          ))}
        </Row>
      )}
    />
  );
};
